import React, { useState, useEffect, useCallback } from 'react';
import PlatformIcon from './PlatformIcon';
import { formatPlatformName } from '../utils/platformHelpers';

// Watch-button colour per platform (brand colours; Kick's green needs dark text).
const WATCH_BUTTON_CLASS = {
  youtube: 'bg-red-600 text-white',
  kick: 'bg-[#00e70a] text-black',
  twitch: 'bg-purple-600 text-white',
};

const LiveStreamMonitor = ({ campaignId, campaignName, gameName, compact = false }) => {
  const [liveStreams, setLiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchLiveStreams = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${campaignId}/live`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLiveStreams(data.streams || []);
        setLastChecked(new Date());
      }
    } catch (err) {
      console.error('Error fetching live streams:', err);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    fetchLiveStreams();

    // Auto-refresh every 60 seconds if enabled
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchLiveStreams, 60000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchLiveStreams, autoRefresh]);

  const formatViewers = (count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  const formatUptime = (startedAt) => {
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <div className={`bg-[#241d33] rounded-xl border border-white ${compact ? 'p-3' : 'p-6'}`}>
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-2.5 h-2.5 bg-white/10 rounded-full"></div>
          <div className="h-4 bg-white/10 rounded w-24"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#241d33] rounded-xl border border-white overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className={`${compact ? 'px-3 py-2' : 'px-4 py-3'} border-b border-pink-500/10 flex items-center justify-between bg-white/3`}>
        <div className="flex items-center gap-2">
          <span className={`${compact ? 'w-2 h-2' : 'w-3 h-3'} rounded-full ${liveStreams.length > 0 ? 'bg-red-500 animate-pulse' : 'bg-white/15'}`}></span>
          <h3 className={`font-medium text-white ${compact ? 'text-sm' : ''}`}>
            Live Now {liveStreams.length > 0 && `(${liveStreams.length})`}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {!compact && (
            <label className="flex items-center gap-2 text-sm text-white">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-white text-sc-pink focus:ring-sc-pink"
              />
              Auto-refresh
            </label>
          )}
          <button
            onClick={fetchLiveStreams}
            className={`text-sc-pink hover:text-sc-pink/80 ${compact ? 'text-xs' : 'text-sm'}`}
          >
            🔄 {compact ? '' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={`${compact ? 'p-3' : 'p-4'} flex-1 flex flex-col`}>
        {liveStreams.length > 0 ? (
          <div className={compact ? 'space-y-2' : 'space-y-3'}>
            {liveStreams.map((stream, index) => {
              const platform = (stream.platform || 'twitch').toLowerCase();
              const streamUrl = stream.streamUrl || (platform === 'youtube'
                ? `https://www.youtube.com/watch?v=${stream.youtubeVideoId}`
                : platform === 'kick'
                  ? `https://kick.com/${stream.kickSlug || stream.userLogin}`
                  : `https://twitch.tv/${stream.userLogin}`);
              const thumbnailUrl = stream.thumbnailUrl
                ?.replace('{width}', '160')
                ?.replace('{height}', '90')
                ?.replace('%{width}', '160')
                ?.replace('%{height}', '90') || '';

              return (
                <a
                  key={index}
                  href={streamUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 ${compact ? 'p-2' : 'p-3'} bg-white/3 rounded-lg hover:bg-white/5 transition-colors`}
                >
                  {/* Thumbnail */}
                  <div className="relative flex-shrink-0">
                    <img
                      src={thumbnailUrl}
                      alt={stream.title}
                      className={`${compact ? 'w-16 h-9' : 'w-24 h-14'} object-cover rounded`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                      }}
                    />
                    <div className={`${compact ? 'w-16 h-9' : 'w-24 h-14'} bg-white/10 rounded items-center justify-center hidden`}>
                      <span className="text-white text-xs">{formatPlatformName(stream.platform) || 'Live'}</span>
                    </div>
                    <span className={`absolute bottom-0.5 left-0.5 px-1 py-0.5 bg-red-600 text-white font-medium rounded ${compact ? 'text-[10px]' : 'text-xs'}`}>
                      LIVE
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-white truncate flex items-center gap-1.5 ${compact ? 'text-sm' : ''}`}>
                      <PlatformIcon platform={platform} size="xs" />
                      <span className="truncate">{stream.userName || stream.creatorName}</span>
                    </p>
                    {!compact && <p className="text-sm text-white truncate">{stream.title}</p>}
                    <div className={`flex items-center gap-2 ${compact ? '' : 'mt-1'} text-xs text-white`}>
                      <span>{formatViewers(stream.viewerCount || 0)} watching</span>
                    </div>
                  </div>

                  {/* CTA */}
                  {!compact && (
                    <div className="flex-shrink-0">
                      <span className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                        WATCH_BUTTON_CLASS[platform] || WATCH_BUTTON_CLASS.twitch
                      }`}>
                        Watch
                      </span>
                    </div>
                  )}
                </a>
              );
            })}
          </div>
        ) : (
          <div className={`text-center ${compact ? 'py-4' : 'py-6'} text-white flex-1 flex flex-col items-center justify-center`}>
            <p className={`${compact ? 'text-lg mb-1' : 'text-2xl mb-2'} text-white`}>🎮</p>
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-white`}>No creators live{compact ? '' : ` with ${gameName || 'this game'}`}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      {lastChecked && !compact && (
        <div className="px-4 py-2 border-t border-white/5 text-xs text-white">
          Last checked: {lastChecked.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};

export default LiveStreamMonitor;
